import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';
import apiService from '../services/apiService';
import ImageUpload from '../components/ImageUpload';

const getApiUrl = (endpoint) => {
  const baseUrl = process.env.NODE_ENV === 'production' ? 'https://fciveria.ge' : 'http://localhost:5000';
  return `${baseUrl}${endpoint}`;
};

const CoachingStaffForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    age: '',
    nationality: 'საქართველო',
    experience: '',
    license: '',
    specialization: '',
    description: '',
    photo_url: '',
    team_type: 'main_staff', // Keep for backward compatibility
    team_assignments: []
  });

  const [positionInputMode, setPositionInputMode] = useState('dropdown'); // 'dropdown' or 'manual'
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const teamTypes = [
    { value: 'main_staff', label: 'პირველი გუნდის შტაბი' },
    { value: 'second_team', label: 'მეორე გუნდის შტაბი' },
    { value: 'womens_team', label: 'ქალთა გუნდის შტაბი' },
    { value: 'youth_academy', label: 'ასაკობრივი ჯგუფების შტაბი' }
  ];

  const licenses = [
    { value: 'A', label: 'A ლიცენზია' },
    { value: 'B', label: 'B ლიცენზია' },
    { value: 'C', label: 'C ლიცენზია' }
  ];

  const positionSuggestions = [
    // Main staff positions
    { value: 'მთავარი მწვრთნელი', label: 'მთავარი მწვრთნელი' },
    { value: 'მწვრთნელის თანაშემწე', label: 'მწვრთნელის თანაშემწე' },
    { value: 'მეკარეების მწვრთნელი', label: 'მეკარეების მწვრთნელი' },
    { value: 'ფიზიკური მომზადების მწვრთნელი', label: 'ფიზიკური მომზადების მწვრთნელი' },
    { value: 'ანალიტიკოსი', label: 'ანალიტიკოსი' },
    
    // Youth academy positions
    { value: 'U9 მწვრთნელი', label: 'U9 მწვრთნელი' },
    { value: 'U10 მწვრთნელი', label: 'U10 მწვრთნელი' },
    { value: 'U11 მწვრთნელი', label: 'U11 მწვრთნელი' },
    { value: 'U12 მწვრთნელი', label: 'U12 მწვრთნელი' },
    { value: 'U13 მწვრთნელი', label: 'U13 მწვრთნელი' },
    { value: 'U14 მწვრთნელი', label: 'U14 მწვრთნელი' },
    { value: 'U15 მწვრთნელი', label: 'U15 მწვრთნელი' },
    { value: 'U17 მწვრთნელი', label: 'U17 მწვრთნელი' },
    { value: 'U19 მწვრთნელი', label: 'U19 მწვრთნელი' },
    { value: 'U21 მწვრთნელი', label: 'U21 მწვრთნელი' },
    { value: 'ყველა U კატეგორია მეკარეების მწვრთნელი', label: 'ყველა U კატეგორია' },
    { value: 'ახალგაზრდული აკადემიის მწვრთნელი', label: 'ახალგაზრდული აკადემიის მწვრთნელი' },
    
    // Women's team positions
    { value: 'ქალთა გუნდის მთავარი მწვრთნელი', label: 'ქალთა გუნდის მთავარი მწვრთნელი' },
    { value: 'ქალთა გუნდის მწვრთნელი', label: 'ქალთა გუნდის მწვრთნელი' },
    { value: 'ქალთა ასაკობრივი ბავშვების მწვრთნელი', label: 'ქალთა ასაკობრივი ბავშვების მწვრთნელი' },
    
    // Second team positions
    { value: 'მეორე გუნდის მთავარი მწვრთნელი', label: 'მეორე გუნდის მთავარი მწვრთნელი' },
    { value: 'მეორე გუნდის მწვრთნელი', label: 'მეორე გუნდის მწვრთნელი' }
  ];

  useEffect(() => {
    if (isEdit) {
      loadStaff();
    }
  }, [id, isEdit]);

  const loadStaff = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('fciveria_admin_user') 
        ? JSON.parse(localStorage.getItem('fciveria_admin_user')).token 
        : null;

      const response = await fetch(getApiUrl(`/api/coaching-staff/${id}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const member = await response.json();
        setFormData({
          name: member.name || '',
          position: member.position || '',
          age: member.age || '',
          nationality: member.nationality || 'საქართველო',
          experience: member.experience || '',
          license: member.license || '',
          specialization: member.specialization || '',
          description: member.description || '',
          photo_url: member.photo_url || '',
          team_type: member.team_type || 'main_staff',
          team_assignments: member.team_assignments || []
        });
      } else {
        console.error('Failed to load coaching staff');
        navigate('/_panel-7c39f8/coaching-staff');
      }
    } catch (error) {
      console.error('Error loading coaching staff:', error);
      navigate('/_panel-7c39f8/coaching-staff');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.position || !formData.team_type) {
      alert('გთხოვთ შეავსოთ ყველა სავალდებულო ველი');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('fciveria_admin_user') 
        ? JSON.parse(localStorage.getItem('fciveria_admin_user')).token 
        : null;

      const url = isEdit ? `/api/coaching-staff/${id}` : '/api/coaching-staff/';
      const method = isEdit ? 'PUT' : 'POST';

      // Prepare data for submission
      const submitData = {
        ...formData,
        age: formData.age ? parseInt(formData.age) : null
      };

      const response = await fetch(getApiUrl(url), {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        navigate('/_panel-7c39f8/coaching-staff');
      } else {
        const error = await response.json();
        alert(error.error || 'შეცდომა მწვრთნელის შენახვისას');
      }
    } catch (error) {
      console.error('Error saving coaching staff:', error);
      alert('შეცდომა მწვრთნელის შენახვისას');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-suggest team assignments based on position
    if (name === 'position' && value) {
      suggestTeamAssignments(value);
    }
  };

  const suggestTeamAssignments = (position) => {
    const suggestions = [];
    
    // Check for main staff positions
    if (position.includes('მთავარი მწვრთნელი') || position.includes('მწვრთნელის თანაშემწე') || 
        position.includes('მეკარეების მწვრთნელი') || position.includes('ფიზიკური მომზადების') ||
        position.includes('ანალიტიკოსი') || position.includes('სპორტული დირექტორი')) {
      suggestions.push({ team_type: 'main_staff', u_category: null, is_primary: true });
    }
    
    // Check for women's team positions
    if (position.includes('ქალთა')) {
      suggestions.push({ team_type: 'womens_team', u_category: null, is_primary: false });
    }
    
    // Check for second team positions
    if (position.includes('მეორე გუნდის')) {
      suggestions.push({ team_type: 'second_team', u_category: null, is_primary: false });
    }
    
    // Check for U categories
    const uCategories = ['U9', 'U10', 'U11', 'U12', 'U13', 'U14', 'U15', 'U17', 'U19', 'U21'];
    uCategories.forEach(category => {
      if (position.includes(category)) {
        suggestions.push({ team_type: 'youth_academy', u_category: category, is_primary: false });
      }
    });
    
    // Check for general youth academy
    if (position.includes('ახალგაზრდული') || position.includes('აკადემია') || position.includes('ყველა U კატეგორია')) {
      suggestions.push({ team_type: 'youth_academy', u_category: 'general', is_primary: false });
    }
    
    // Update team assignments if suggestions were found
    if (suggestions.length > 0) {
      setFormData(prev => ({
        ...prev,
        team_assignments: suggestions
      }));
    }
  };

  const handleTeamAssignmentChange = (teamType, uCategory = null, isPrimary = false) => {
    setFormData(prev => {
      const existingAssignments = [...prev.team_assignments];
      const existingIndex = existingAssignments.findIndex(
        assignment => assignment.team_type === teamType && assignment.u_category === uCategory
      );

      if (existingIndex >= 0) {
        // Remove existing assignment
        existingAssignments.splice(existingIndex, 1);
      } else {
        // Add new assignment
        existingAssignments.push({
          team_type: teamType,
          u_category: uCategory,
          is_primary: isPrimary
        });
      }

      return {
        ...prev,
        team_assignments: existingAssignments
      };
    });
  };

  const isTeamAssigned = (teamType, uCategory = null) => {
    return formData.team_assignments.some(
      assignment => assignment.team_type === teamType && assignment.u_category === uCategory
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-georgian">იტვირთება...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/_panel-7c39f8/coaching-staff')}
                className="mr-4 text-gray-400 hover:text-gray-600"
              >
                <ArrowLeftIcon className="h-6 w-6" />
              </button>
              <AcademicCapIcon className="h-8 w-8 text-primary-600 mr-3" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900 font-georgian">
                  {isEdit ? 'მწვრთნელის რედაქტირება' : 'ახალი მწვრთნელი'}
                </h1>
                <p className="text-sm text-gray-600 font-georgian">
                  {isEdit ? 'მწვრთნელის ინფორმაციის განახლება' : 'ახალი მწვრთნელის დამატება'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto py-6 sm:px-6 lg:px-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 font-georgian mb-6">
                ძირითადი ინფორმაცია
              </h3>
              
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    სახელი და გვარი *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="მწვრთნელის სახელი"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    პოზიცია *
                  </label>
                  
                  {/* Input Mode Toggle */}
                  <div className="mb-2">
                    <div className="flex space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="positionMode"
                          value="dropdown"
                          checked={positionInputMode === 'dropdown'}
                          onChange={(e) => setPositionInputMode(e.target.value)}
                          className="text-orange-600 focus:ring-orange-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 font-georgian">აირჩიეთ სიიდან</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="positionMode"
                          value="manual"
                          checked={positionInputMode === 'manual'}
                          onChange={(e) => setPositionInputMode(e.target.value)}
                          className="text-orange-600 focus:ring-orange-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 font-georgian">ჩაწერეთ ხელით</span>
                      </label>
                    </div>
                  </div>

                  {/* Position Input */}
                  {positionInputMode === 'dropdown' ? (
                    <select
                      name="position"
                      value={formData.position}
                      onChange={handleChange}
                      required
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">აირჩიეთ პოზიცია</option>
                      {positionSuggestions.map(position => (
                        <option key={position.value} value={position.value}>
                          {position.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      name="position"
                      value={formData.position}
                      onChange={handleChange}
                      required
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      placeholder="ჩაწერეთ პოზიცია ხელით"
                    />
                  )}
                  
                  {/* Auto-suggestion notice */}
                  {positionInputMode === 'manual' && (
                    <p className="mt-1 text-xs text-gray-500 font-georgian">
                      💡 რჩევა: თუ პოზიციაში შეიცავს "U9", "ქალთა", "მთავარი მწვრთნელი" და ა.შ., სისტემა ავტომატურად შესთავაზებს შტაბის მინიჭებას
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    ასაკი
                  </label>
                  <input
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleChange}
                    min="1"
                    max="80"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="ასაკი წლებში"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    ეროვნება
                  </label>
                  <input
                    type="text"
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="ეროვნება"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    გამოცდილება
                  </label>
                  <input
                    type="text"
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="მაგ: 8 წელი"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    ლიცენზია
                  </label>
                  <select
                    name="license"
                    value={formData.license}
                    onChange={handleChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">აირჩიეთ ლიცენზია</option>
                    {licenses.map(license => (
                      <option key={license.value} value={license.value}>
                        {license.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    სპეციალიზაცია
                  </label>
                  <input
                    type="text"
                    name="specialization"
                    value={formData.specialization}
                    onChange={handleChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="სპეციალიზაცია"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    შტაბები (შეგიძლიათ რამდენიმე აირჩიოთ) *
                  </label>
                  
                  {/* Main Staff */}
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-800 font-georgian mb-2">მთავარი შტაბი</h4>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={isTeamAssigned('main_staff')}
                          onChange={() => handleTeamAssignmentChange('main_staff', null, true)}
                          className="rounded border-gray-300 text-orange-600 shadow-sm focus:border-orange-300 focus:ring focus:ring-orange-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm text-gray-700 font-georgian">პირველი გუნდის შტაბი</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={isTeamAssigned('second_team')}
                          onChange={() => handleTeamAssignmentChange('second_team', null, false)}
                          className="rounded border-gray-300 text-orange-600 shadow-sm focus:border-orange-300 focus:ring focus:ring-orange-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm text-gray-700 font-georgian">მეორე გუნდის შტაბი</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={isTeamAssigned('womens_team')}
                          onChange={() => handleTeamAssignmentChange('womens_team', null, false)}
                          className="rounded border-gray-300 text-orange-600 shadow-sm focus:border-orange-300 focus:ring focus:ring-orange-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm text-gray-700 font-georgian">ქალთა გუნდის შტაბი</span>
                      </label>
                    </div>
                  </div>

                  {/* Youth Academy */}
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-800 font-georgian mb-2">ასაკობრივი ჯგუფები</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {['U9', 'U10', 'U11', 'U12', 'U13', 'U14', 'U15', 'U17', 'U19', 'U21'].map(category => (
                        <label key={category} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={isTeamAssigned('youth_academy', category)}
                            onChange={() => handleTeamAssignmentChange('youth_academy', category, false)}
                            className="rounded border-gray-300 text-orange-600 shadow-sm focus:border-orange-300 focus:ring focus:ring-orange-200 focus:ring-opacity-50"
                          />
                          <span className="ml-2 text-sm text-gray-700 font-georgian">{category}</span>
                        </label>
                      ))}
                    </div>
                    <label className="flex items-center mt-2">
                      <input
                        type="checkbox"
                        checked={isTeamAssigned('youth_academy', 'general')}
                        onChange={() => handleTeamAssignmentChange('youth_academy', 'general', false)}
                        className="rounded border-gray-300 text-orange-600 shadow-sm focus:border-orange-300 focus:ring focus:ring-orange-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700 font-georgian">ყველა U კატეგორია</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <ImageUpload
                  value={formData.photo_url}
                  onChange={(url) => setFormData(prev => ({ ...prev, photo_url: url }))}
                  category="photos"
                  label="მწვრთნელის ფოტო"
                  required={false}
                  maxSize={50}
                />
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                  აღწერა
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="მწვრთნელის აღწერა..."
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/_panel-7c39f8/coaching-staff')}
              className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md text-sm font-medium font-georgian"
            >
              გაუქმება
            </button>
            <button
              type="submit"
              disabled={saving}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium font-georgian disabled:opacity-50"
            >
              {saving ? 'შენახვა...' : (isEdit ? 'განახლება' : 'დამატება')}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default CoachingStaffForm;
